import Image from 'next/image';
import type { ResolvedTeacher } from './types';
import { useRenderContext } from '@/lib/render/RenderContextProvider';

function formatSubjects(subjects: string[]): string {
  if (subjects.length === 0) {
    return '';
  }
  if (subjects.length === 1) {
    return subjects[0];
  }
  return (
    subjects.slice(0, -1).join(', ') + ' und ' + subjects[subjects.length - 1]
  );
}

interface TeacherCardProps {
  teacher: ResolvedTeacher;
}

export const TeacherCard = ({ teacher }: TeacherCardProps) => {
  const { imageUrl } = useRenderContext();
  const src = teacher.image ? imageUrl({ url: teacher.image }) : null;
  // Teachers are shown by first name only. Deliberately not falling back to
  // `name`, which the NHL sync fills as "First Last" (see TeacherMapper).
  const displayName = teacher.firstName ?? '';

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-4 h-[276px] w-[276px] overflow-hidden rounded-full bg-gray-200">
        {src ? (
          <Image
            src={src}
            // The partner's own alt text when they wrote one, the first name
            // otherwise — which is what the card always used (SH-344 item 5.2).
            // Not substituted: the portal field takes no placeholders, and a
            // literal token in an `alt` is read out to screen-reader users.
            alt={teacher.imageAltText || displayName}
            {...(teacher.imageTitle ? { title: teacher.imageTitle } : {})}
            width={276}
            height={276}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-8xl text-gray-400">
            👤
          </div>
        )}
      </div>
      {displayName && <p className="h3 mb-1 font-medium">{displayName}</p>}
      {teacher.subjects && teacher.subjects.length > 0 && (
        <p className="text-muted text-sm">
          gibt Nachhilfe in {formatSubjects(teacher.subjects)}
        </p>
      )}
      {teacher.aboutMe && (
        <p className="text-muted mt-2 text-xs">{teacher.aboutMe}</p>
      )}
    </div>
  );
};
